import { useMemo } from 'react';
import { observer, useLocalObservable } from 'mobx-react';
import { formatDateDDMMYYYY } from '../../../utils/utils';
import IconButton, { ICON_BUTTON_TYPES } from '../../../components/buttons/IconButton';
import { paths } from '../../../utils/constants/routes';
import { buildProjectResultsUrl } from '../../../utils/profiler/buildProjectResultsUrl';
import { useStore } from '../../../store/store';
import { SELECTED_PROJECT_TABS, SHARED_PROJECT_TYPE_TABS } from './constants';
import {
  PROFILER_INDUSTRY_CODES_SEARCH_FIELDS,
  PROFILER_SEARCH_FIELDS
} from '../../../utils/constants/fields';
import pubSub from '../../../utils/pubsub';
import ChipsList from '../../../components/common/ChipsList';
import { UI_OPTIONS } from '../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../utils/constants/uiOptionKeys';
import { useProjectRowOpen } from '../../../hooks/useProjectRowOpen';
import {
  isBulkSearchProject,
  bulkSearchCriteriaText
} from '../../ProfilerProjects/components/bulkSearchCriteria';

/**
 * Industries / criteria cell for a shared-project row.
 * Bulk uploads have no mapped `fields` (see ProfilerProjectsStore) — show the
 * source-file line instead of reading project.fields (TD-2864).
 */
const IndustriesCell = ({ project, utilsStore }) => {
  if (isBulkSearchProject(project.searchCategory)) {
    return bulkSearchCriteriaText(project);
  }

  const list =
    project.searchCategory === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Industries']
      ? utilsStore.getIndustryCodesDisplayNames(
          project.fields?.[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.INDUSTRY_CODES.NAME]
        )
      : (project.fields?.[PROFILER_SEARCH_FIELDS.INDUSTRY.NAME] || '')
          .split(',')
          .map((str) => str.trim())
          .filter(Boolean);

  return <ChipsList list={list} />;
};

const ProfilerSharedProjectEntry = observer(({ project, params, isFromAgentView, index }) => {
  const { authStore, utilsStore } = useStore();
  const state = useLocalObservable(() => ({
    get layout() {
      return utilsStore.windowWidth < 1201 ? 'mobile' : 'desktop';
    }
  }));

  const getRequestUrl = () =>
    buildProjectResultsUrl(
      isFromAgentView ? paths.TEAM : paths.PROFILER_TEAM,
      params,
      project.id,
      SELECTED_PROJECT_TABS.RESULTS
    );

  const { onClick: onOpenRequestClick, linkContextMenu: requestMenu } = useProjectRowOpen(
    getRequestUrl,
    () => pubSub.publish({ event: 'team-saved-project-list-scroll-y' })
  );

  const foundProjectUser = useMemo(() => {
    const foundUser = authStore.companyUsers.find((u) => u.id === project.ownerId);
    if (foundUser) {
      return `${foundUser.firstName} ${foundUser.lastName}`;
    }
    return 'unknown';
  }, [project, authStore]);

  const foundUser = useMemo(() => {
    return project.sharedUsers.find((user) => user.id === authStore.currentUserId);
  }, [project, authStore]);

  if (state.layout === 'mobile') {
    return (
      <div className="mobile-project-entry" style={index === 0 ? { marginTop: '8px' } : {}}>
        <div className="project-name">{project.name}</div>
        <div className="project-property-row">
          <div className="project-property-field">Sender name</div>
          <div className="project-property-value">{foundProjectUser}</div>
        </div>
        <div className="project-property-row">
          <div className="project-property-field">Profile</div>
          <div className="project-property-value">Corporate</div>
        </div>
        <div className="project-property-row">
          <div className="project-property-field">Industries</div>
          <div className="project-property-value">
            <IndustriesCell project={project} utilsStore={utilsStore} />
          </div>
        </div>
        <div className="project-property-row">
          <div className="project-property-field">Results</div>
          <div className="project-property-value">{project.resultsCount}</div>
        </div>
        <div className="project-property-row">
          <div className="project-property-field">
            {params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.PENDING
              ? 'Date received'
              : 'Date resolved'}
          </div>
          <div className="project-property-value">
            {formatDateDDMMYYYY(
              params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.PENDING
                ? foundUser?.dateReceived
                : foundUser?.dateResolved
            )}
          </div>
        </div>

        <div className="project-actions-row">
          <IconButton
            type={ICON_BUTTON_TYPES.BLUE}
            innerText="Open request"
            onClick={onOpenRequestClick}
            filled
            wrapClassName="open-feedback-request-btn"
            linkContextMenu={requestMenu}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="row" style={index === 0 ? { marginTop: '8px' } : {}}>
      <div className="item">
        <div className="label">Sender Name</div>
        <div className="value">{foundProjectUser}</div>
      </div>
      <div className="item">
        <div className="label">Project name</div>
        <div className="value">{project.name}</div>
      </div>
      <div className="item">
        <div className="label">Profile</div>
        <div className="value">Corporate</div>
      </div>
      <div className="item">
        <div className="label">Industries</div>
        <div className="value">
          <IndustriesCell project={project} utilsStore={utilsStore} />
        </div>
      </div>
      <div className="item">
        <div className="label">Results</div>
        <div className="value">{project.resultsCount}</div>
      </div>
      <div className="item">
        <div className="label">
          {params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.PENDING
            ? 'Date received'
            : 'Date resolved'}
        </div>
        <div className="value">
          {formatDateDDMMYYYY(
            params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.PENDING
              ? foundUser?.dateReceived
              : foundUser?.dateResolved
          )}
        </div>
      </div>
      <div className="actions">
        <IconButton
          type={ICON_BUTTON_TYPES.BLUE}
          innerText="Open request"
          onClick={onOpenRequestClick}
          filled
          linkContextMenu={requestMenu}
        />
      </div>
    </div>
  );
});

export default ProfilerSharedProjectEntry;
